import { createGlobalStyle } from 'styled-components';

/**
 * Variáveis de tema (light e dark).
 *
 * Como usar:
 *  1) Coloque <GlobalStyle /> uma vez, dentro do <ThemeProvider> (no App).
 *  2) Nos style.js, troque as CONSTANTES de cor por var(--nome) (ver palette.js).
 *
 * O dark é ativado pela classe `theme-dark` no <html> (o ThemeContext já faz isso).
 * Como todas as telas usam as mesmas variáveis, TODAS respondem ao tema de uma vez.
 */
export const GlobalStyle = createGlobalStyle`
  :root {
    /* superfícies / fundo */
    --bg: #f6f8f9;
    --bg-glow: #eaf2f4;          /* brilho radial claro do topo */
    --surface: #ffffff;          /* cartões brancos */
    --surface-2: #f6f8f9;        /* inputs, chips */
    --line: #e3e8eb;             /* bordas */

    /* texto */
    --text: #16323b;             /* texto forte */
    --text-2: #34474d;           /* texto médio */
    --text-muted: #5b6b72;       /* rótulos/legendas */

    /* identidade (mantidas nos dois temas) */
    --teal: #003e4f;
    --teal-light: #0a5a73;
    --teal-soft: #e6f0f3;
    --teal-glow: rgba(0, 62, 79, 0.18);
    --coral: #e07a5f;

    /* teal usado como COR DE TEXTO (títulos, nomes) — no claro é o próprio teal;
       no escuro fica claro para ter contraste sobre os cartões escuros.
       Use var(--teal-text) para texto e mantenha var(--teal) para FUNDOS. */
    --teal-text: #003e4f;

    /* status */
    --green: #1d8b5a;
    --green-soft: #e3f4ec;
    --red: #cf3a3a;
    --red-soft: #fbe9e9;

    /* skeleton */
    --skel-a: #eef2f5;
    --skel-b: #f6f8f9;
  }

  html.theme-dark {
    --bg: #0f1720;
    --bg-glow: #14202c;
    --surface: #161f2b;          /* cartões escuros */
    --surface-2: #1e2937;        /* inputs escuros */
    --line: #2c3a4a;

    --text: #e6edf3;
    --text-2: #c4d0da;
    --text-muted: #9fb1c1;

    /* identidade: teal fica um pouco mais claro para respirar no escuro */
    --teal: #0e5566;
    --teal-light: #157089;
    --teal-soft: #17303a;        /* fundo suave dos chips no dark */
    --teal-glow: rgba(0, 0, 0, 0.4);
    --coral: #e07a5f;

    /* texto teal claro o suficiente para ler sobre cartões escuros */
    --teal-text: #59c2d6;

    --green: #3fbf7f;
    --green-soft: #14312a;
    --red: #ff6b6b;
    --red-soft: #3a1f22;

    --skel-a: #1a2431;
    --skel-b: #222e3c;
  }

  /* fundo base da aplicação segue o tema */
  html.theme-dark body,
  html.theme-dark .app,
  html.theme-dark .container {
    background-color: var(--bg);
    color: var(--text);
  }

  /*
   * FORÇA TEMA CLARO em telas específicas (diários, boletins, histórico,
   * ficha anual). O ScrollToTop (AppRoutes) adiciona a classe .force-light
   * no <html> quando a rota é uma dessas. Como este bloco vem DEPOIS do
   * html.theme-dark, ele vence e reverte todas as variáveis para o claro,
   * mesmo com o tema escuro ativado.
   */
  html.force-light {
    --bg: #f6f8f9;
    --bg-glow: #eaf2f4;
    --surface: #ffffff;
    --surface-2: #f6f8f9;
    --line: #e3e8eb;

    --text: #16323b;
    --text-2: #34474d;
    --text-muted: #5b6b72;

    --teal: #003e4f;
    --teal-light: #0a5a73;
    --teal-soft: #e6f0f3;
    --teal-glow: rgba(0, 62, 79, 0.18);
    --coral: #e07a5f;
    --teal-text: #003e4f;

    --green: #1d8b5a;
    --green-soft: #e3f4ec;
    --red: #cf3a3a;
    --red-soft: #fbe9e9;

    --skel-a: #eef2f5;
    --skel-b: #f6f8f9;
  }

  html.force-light body,
  html.force-light .app,
  html.force-light .container {
    background-color: var(--bg);
    color: var(--text);
  }
`;

export default GlobalStyle;
